// ═══════════════════════════════════════════════════════════════════════════
// BravoReportDashboard.js — Romeo-style analytics dashboard for Bravo entries
// Renders inside BravoDashboard.js TAB 3 ("Report Dashboard") via <BravoReportDashboard embedded />
// Backend: bravo.php?action=report (extended with kpi/dailyTrend/contractorPerf/etc.)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

// ── Helpers ──────────────────────────────────────────────────────────────────
const TODAY       = new Date().toISOString().slice(0, 10);
const MONTH_START = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
const fmt   = (n, d = 2) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtN  = (n) => Number(n || 0).toLocaleString();
const fmtK  = (n) => { const x = Number(n) || 0; return x >= 1000 ? (x / 1000).toFixed(1) + 'K' : String(x); };
const fmtDate = (d) => { if (!d) return '—'; const dt = new Date(d + 'T00:00:00'); return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };
const clr   = ['#1565c0','#d32f2f','#059669','#d97706','#7b1fa2','#0891b2','#dc2626','#16a34a'];

// ── SVG Bar Chart (cloned from Romeo for visual + reliability parity) ──
const BarChart = ({ data = [], xKey, bars = [], colors = clr, height = 220, horizontal = false }) => {
  if (!data.length) return <div className="brd-chart-empty"><span>📊</span><span>No data for selected filters</span></div>;
  const W = 520, pL = horizontal ? 110 : 48, pB = 52, pT = 18, pR = 12;
  const cW = W - pL - pR, cH = height - pB - pT;
  const maxVal = Math.max(...data.flatMap(d => bars.map(b => parseFloat(d[b.key]) || 0)), 1);
  const gW = horizontal ? cH / data.length : cW / data.length;
  const bW = Math.max(6, Math.min(28, (gW / bars.length) - 6));
  const gridLines = [0, 0.25, 0.5, 0.75, 1];
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
      {gridLines.map(t => {
        if (horizontal) {
          const xx = pL + cW * t;
          return <g key={t}>
            <line x1={xx} y1={pT} x2={xx} y2={pT + cH} stroke="#e2e6ea" strokeWidth="1" />
            <text x={xx} y={pT + cH + 14} textAnchor="middle" fontSize="9" fill="#9ca3af">{(maxVal * t).toFixed(0)}</text>
          </g>;
        }
        const yy = pT + cH * (1 - t);
        return <g key={t}>
          <line x1={pL} y1={yy} x2={W - pR} y2={yy} stroke="#e2e6ea" strokeWidth="1" />
          <text x={pL - 5} y={yy + 4} textAnchor="end" fontSize="9" fill="#9ca3af">{(maxVal * t).toFixed(0)}</text>
        </g>;
      })}
      {data.map((d, gi) => {
        if (horizontal) {
          // Horizontal bars
          const totalGap = bars.length * bW + (bars.length - 1) * 3;
          const yStart   = pT + gi * gW + (gW - totalGap) / 2;
          return (
            <g key={gi}>
              {bars.map((bar, bi) => {
                const val = parseFloat(d[bar.key]) || 0;
                const bL  = (val / maxVal) * cW;
                const y   = yStart + bi * (bW + 3);
                return (
                  <g key={bi}>
                    <rect x={pL} y={y} width={Math.max(bL, 2)} height={bW} fill={colors[bi % colors.length]} rx="3" opacity="0.88">
                      <title>{d[xKey]} — {bar.label}: {val.toFixed(1)}</title>
                    </rect>
                    {bL > 30 && <text x={pL + bL - 5} y={y + bW / 2 + 3} textAnchor="end" fontSize="9" fill="#fff" fontWeight="600">{val > 999 ? (val/1000).toFixed(1)+'k' : val.toFixed(0)}</text>}
                  </g>
                );
              })}
              <text x={pL - 6} y={yStart + totalGap / 2 + 3} textAnchor="end" fontSize="10" fill="#374151">
                {String(d[xKey] || '').length > 14 ? String(d[xKey]).slice(0, 14) + '…' : d[xKey]}
              </text>
            </g>
          );
        }
        const midX = pL + gi * gW + gW / 2;
        return (
          <g key={gi}>
            {bars.map((bar, bi) => {
              const val = parseFloat(d[bar.key]) || 0;
              const bh  = (val / maxVal) * cH;
              const totalGap = bars.length * bW + (bars.length - 1) * 3;
              const x = pL + gi * gW + (gW - totalGap) / 2 + bi * (bW + 3);
              const y = pT + cH - bh;
              return (
                <g key={bi}>
                  <rect x={x} y={y} width={bW} height={Math.max(bh, 2)} fill={colors[bi % colors.length]} rx="3" opacity="0.88">
                    <title>{d[xKey]} — {bar.label}: {val.toFixed(1)}</title>
                  </rect>
                  {bh > 16 && <text x={x + bW / 2} y={y + 11} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="600">{val > 999 ? (val/1000).toFixed(1)+'k' : val.toFixed(0)}</text>}
                </g>
              );
            })}
            <text x={midX} y={height - 6} textAnchor="middle" fontSize="9" fill="#6b7280">
              {String(d[xKey] || '').length > 9 ? String(d[xKey]).slice(0, 9) + '…' : d[xKey]}
            </text>
          </g>
        );
      })}
      {/* Legend (multi-bar only) */}
      {bars.length > 1 && bars.map((b, bi) => (
        <g key={bi} transform={`translate(${pL + bi * 95}, ${pT - 8})`}>
          <rect x="0" y="-7" width="10" height="10" fill={colors[bi % colors.length]} rx="2" />
          <text x="14" y="2" fontSize="9" fill="#6b7280">{b.label}</text>
        </g>
      ))}
      <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
      <line x1={pL} y1={pT + cH} x2={W - pR} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
    </svg>
  );
};

// ── SVG Line Chart (cloned from Romeo) ──
const LineChart = ({ data = [], xKey, lines = [], colors = clr, height = 200 }) => {
  if (!data.length) return <div className="brd-chart-empty"><span>📈</span><span>No trend data available</span></div>;
  const W = 520, pL = 48, pB = 42, pT = 18, pR = 12;
  const cW = W - pL - pR, cH = height - pB - pT;
  const allVals = data.flatMap(d => lines.map(l => parseFloat(d[l.key]) || 0));
  const maxV = Math.max(...allVals, 1), minV = 0;
  const X = (i) => pL + (data.length < 2 ? cW / 2 : (i / (data.length - 1)) * cW);
  const Y = (v) => pT + cH - ((v - minV) / (maxV - minV)) * cH;
  const step = Math.max(1, Math.ceil(data.length / 7));
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const yy = pT + cH * (1 - t);
        return <g key={t}>
          <line x1={pL} y1={yy} x2={W - pR} y2={yy} stroke="#e2e6ea" strokeWidth="1" />
          <text x={pL - 5} y={yy + 4} textAnchor="end" fontSize="9" fill="#9ca3af">{(maxV * t).toFixed(1)}</text>
        </g>;
      })}
      {lines.map((line, li) => {
        const pts = data.map((d, i) => `${X(i)},${Y(parseFloat(d[line.key]) || 0)}`).join(' ');
        return (
          <g key={li}>
            <polyline points={pts} fill="none" stroke={colors[li % colors.length]} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
            {data.map((d, i) => {
              const val = parseFloat(d[line.key]) || 0;
              return (
                <circle key={i} cx={X(i)} cy={Y(val)} r="3.5" fill={colors[li % colors.length]} stroke="#fff" strokeWidth="1.5">
                  <title>{fmtDate(d[xKey])}: {val.toFixed(2)}</title>
                </circle>
              );
            })}
          </g>
        );
      })}
      {data.map((d, i) => {
        if (i % step !== 0 && i !== data.length - 1) return null;
        return (
          <text key={i} x={X(i)} y={height - 5} textAnchor="middle" fontSize="9" fill="#6b7280">
            {String(d[xKey] || '').slice(5)}
          </text>
        );
      })}
      {lines.length > 1 && lines.map((l, li) => (
        <g key={li} transform={`translate(${pL + li * 100}, ${pT - 6})`}>
          <line x1="0" y1="0" x2="16" y2="0" stroke={colors[li % colors.length]} strokeWidth="2" />
          <circle cx="8" cy="0" r="3" fill={colors[li % colors.length]} />
          <text x="20" y="4" fontSize="9" fill="#6b7280">{l.label}</text>
        </g>
      ))}
      <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
      <line x1={pL} y1={pT + cH} x2={W - pR} y2={pT + cH} stroke="#d1d5db" strokeWidth="1" />
    </svg>
  );
};

// ── Inline CSS (scoped via brd- prefix to avoid conflicting with Bravo or Romeo) ──
const CSS = `
.brd-wrap { padding: 0; }
.brd-section-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 12px; background:rgba(124,58,237,.08); border-radius:20px; border:1px solid rgba(124,58,237,.2); margin-bottom:8px; }
.brd-section-pill span { font-size:11px; color:#7c3aed; font-weight:700; letter-spacing:.3px; }
.brd-h1 { font-size:18px; font-weight:700; color:#111827; }
.brd-livesync { display:inline-flex; align-items:center; gap:4px; margin-left:10px; padding:2px 9px; border-radius:20px; background:rgba(5,150,105,.1); border:1px solid rgba(5,150,105,.2); font-size:10px; font-weight:600; color:#059669; vertical-align:middle; }
.brd-sub { font-size:12px; color:#6b7280; margin-bottom:14px; }

.brd-filter-bar { background:#fff; border:1px solid #e2e6ea; border-radius:10px; padding:14px; display:flex; gap:14px; align-items:flex-end; flex-wrap:wrap; margin-bottom:14px; }
.brd-ff { display:flex; flex-direction:column; gap:4px; }
.brd-ff label { font-size:10px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.4px; }
.brd-ff input, .brd-ff select { padding:7px 10px; border:1px solid #d1d5db; border-radius:6px; font-family:inherit; font-size:12px; background:#fff; min-width:120px; }
.brd-apply-btn { padding:8px 16px; border:none; border-radius:6px; background:linear-gradient(135deg,#d32f2f,#1565c0); color:#fff; font-size:12px; font-weight:700; cursor:pointer; }
.brd-reset-btn { padding:8px 14px; border:1px solid #d1d5db; border-radius:6px; background:#fff; color:#374151; font-size:12px; font-weight:700; cursor:pointer; }

.brd-preset-grp { display:inline-flex; gap:4px; padding:3px; background:#f8f9fa; border:1px solid #e2e6ea; border-radius:7px; }
.brd-preset-btn { padding:5px 11px; border-radius:5px; border:none; background:transparent; color:#6b7280; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; }
.brd-preset-btn.active { background:linear-gradient(135deg,#d32f2f,#1565c0); color:#fff; }

.brd-export-row { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.brd-exp-btn { padding:7px 14px; border:none; border-radius:6px; font-size:11px; font-weight:700; cursor:pointer; font-family:inherit; }
.brd-exp-btn.full { background:linear-gradient(135deg,#7c3aed,#1565c0); color:#fff; }
.brd-exp-btn.cont { background:#dbeafe; color:#1565c0; border:1px solid #93c5fd; }
.brd-exp-btn.lot  { background:#ecfdf5; color:#065f46; border:1px solid #6ee7b7; }

.brd-kpi-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:10px; margin-bottom:14px; }
.brd-kpi-card { background:#fff; border:1px solid #e2e6ea; border-left:4px solid; border-radius:10px; padding:11px 14px; display:flex; align-items:center; gap:9px; }
.brd-kpi-icon { font-size:22px; }
.brd-kpi-lbl { font-size:9px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.4px; }
.brd-kpi-val { font-size:18px; font-weight:700; font-family:'DM Mono',monospace; }
.brd-kpi-card.red    { border-left-color:#d32f2f; } .brd-kpi-card.red    .brd-kpi-val { color:#d32f2f; }
.brd-kpi-card.blue   { border-left-color:#1565c0; } .brd-kpi-card.blue   .brd-kpi-val { color:#1565c0; }
.brd-kpi-card.green  { border-left-color:#059669; } .brd-kpi-card.green  .brd-kpi-val { color:#059669; }
.brd-kpi-card.amber  { border-left-color:#d97706; } .brd-kpi-card.amber  .brd-kpi-val { color:#d97706; }
.brd-kpi-card.purple { border-left-color:#7c3aed; } .brd-kpi-card.purple .brd-kpi-val { color:#7c3aed; }

.brd-card { background:#fff; border:1px solid #e2e6ea; border-radius:10px; margin-bottom:14px; }
.brd-card-hdr { display:flex; align-items:center; padding:14px; gap:10px; border-bottom:1px solid #f1f5f9; }
.brd-card-title { font-size:14px; font-weight:700; color:#111827; }
.brd-pill { padding:3px 10px; border-radius:12px; font-size:11px; font-weight:700; }
.brd-pill.blue   { background:rgba(21,101,192,.08); border:1px solid rgba(21,101,192,.2); color:#1565c0; }
.brd-pill.green  { background:#ecfdf5; border:1px solid #6ee7b7; color:#065f46; }

.brd-charts-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
@media (max-width:900px) { .brd-charts-row { grid-template-columns:1fr; } }
.brd-chart-wrap { padding:14px; min-height:280px; position:relative; }
.brd-chart-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; color:#9ca3af; height:240px; font-size:12px; }
.brd-chart-empty span:first-child { font-size:36px; opacity:.4; }

.brd-tbl-wrap { padding:0 14px 14px; overflow-x:auto; }
.brd-tbl { width:100%; border-collapse:collapse; font-size:11px; }
.brd-tbl thead tr { background:#f8f9fa; border-bottom:1.5px solid #e2e6ea; }
.brd-tbl th { padding:10px 12px; font-weight:700; font-size:10px; color:#374151; text-transform:uppercase; letter-spacing:.3px; text-align:left; }
.brd-tbl th.r { text-align:right; }
.brd-tbl th.c { text-align:center; }
.brd-tbl tbody tr { border-bottom:1px solid #f1f5f9; }
.brd-tbl tbody tr:hover { background:#fafffd; }
.brd-tbl td { padding:9px 12px; }
.brd-tbl td.r { text-align:right; font-family:'DM Mono',monospace; }
.brd-tbl td.c { text-align:center; }
.brd-tbl tfoot tr { background:#f0f2f5; border-top:2px solid #e2e6ea; font-weight:700; }
.brd-tbl tfoot td { padding:10px 12px; }
`;

// Inject CSS once
let cssInjected = false;
const ensureCSS = () => {
  if (cssInjected) return;
  const tag = document.createElement('style');
  tag.id = 'brd-styles';
  tag.textContent = CSS;
  document.head.appendChild(tag);
  cssInjected = true;
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const BravoReportDashboard = ({ embedded = false }) => {
  const { user } = usePPCAuth();
  const auth = useCallback(() => {
    const t = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${t}` } };
  }, []);

  // ── State ────────────────────────────────────────────────────────────────
  const [dateFrom,    setDateFrom]    = useState(MONTH_START);
  const [dateTo,      setDateTo]      = useState(TODAY);
  const [filterCont,  setFilterCont]  = useState('');
  const [filterShift, setFilterShift] = useState('');
  const [applied, setApplied] = useState({ from: MONTH_START, to: TODAY, cont: '', shift: '' });
  const [quickPreset, setQuickPreset] = useState('monthly');

  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(null);
  const [chartTab, setChartTab] = useState('hon');   // 'hon' | 'hl' | 'tokens'

  // FIX: Charts now rendered via inline SVG components — no Chart.js refs needed.

  // ── Inject CSS on mount ──────────────────────────────────────────────────
  useEffect(() => { ensureCSS(); }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (a) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        from: a.from,
        to:   a.to,
        contractors: a.cont || 'all',
      });
      if (a.shift) params.set('shift', a.shift);
      const res = await axios.get(`${PPC_API_URL}/bravo.php?action=report&${params}`, auth());
      if (res.data?.success) {
        setData(res.data);
      } else {
        console.error('Bravo report fetch returned non-success:', res.data);
        setData(null);
      }
    } catch (e) {
      console.error('Bravo report fetch error:', e.response?.data || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { fetchData(applied); }, [applied, fetchData]);

  // ── Filter actions ───────────────────────────────────────────────────────
  const applyFilters = () => {
    setApplied({ from: dateFrom, to: dateTo, cont: filterCont, shift: filterShift });
  };

  const applyPreset = (preset) => {
    const today = new Date();
    let from, to;
    if (preset === 'daily') {
      from = to = today.toISOString().slice(0, 10);
    } else if (preset === 'weekly') {
      const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - ((day + 6) % 7));
      const sun = new Date(mon);   sun.setDate(mon.getDate() + 6);
      from = mon.toISOString().slice(0, 10);
      to   = sun.toISOString().slice(0, 10);
    } else if (preset === 'monthly') {
      const y = today.getFullYear(), m = today.getMonth();
      from = new Date(y, m, 1).toISOString().slice(0, 10);
      to   = new Date(y, m + 1, 0).toISOString().slice(0, 10);
    } else { return; }
    setQuickPreset(preset);
    setDateFrom(from);
    setDateTo(to);
    setApplied({ from, to, cont: filterCont, shift: filterShift });
  };

  const resetFilters = () => {
    setDateFrom(MONTH_START); setDateTo(TODAY);
    setFilterCont(''); setFilterShift('');
    setApplied({ from: MONTH_START, to: TODAY, cont: '', shift: '' });
    setQuickPreset('monthly');
  };

  // FIX: Chart.js useEffect blocks removed — charts now use inline SVG.

  // ── Excel export (uses XLSX if available, else CSV fallback) ─────────────
  const downloadCSV = (filename, headers, rows) => {
    const csv = [headers, ...rows].map(r => r.map(v => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const exportFull = () => {
    if (!data) return;
    const k = data.kpi || {};
    const rows = [
      ['Bravo Reports', `${applied.from} to ${applied.to}`],
      [],
      ['Total Contractors', k.tc || 0],
      ['Total Associates',  k.ta || 0],
      ['Total Tokens',      k.tt || 0],
      ['Total H.ON (KG)',   k.tHON || 0],
      ['Total Deheading (KG)', k.tHL || 0],
      ['Total H.ON Count',  k.tHonCount || 0],
      ['Total Entries',     k.tEntries || 0],
      ['Avg Yield %',       k.avgYield || 0],
      ['Avg Tokens/Assoc',  k.avgTokPerAssoc || 0],
    ];
    downloadCSV(`bravo_full_${applied.from}_${applied.to}.csv`, ['Metric','Value'], rows);
  };

  const exportContractors = () => {
    if (!data?.contractorPerf) return;
    downloadCSV(
      `bravo_contractors_${applied.from}_${applied.to}.csv`,
      ['Contractor','H.ON Qty','Deheading','H.ON Count','Tokens','Associates','Entries','Yield %','% of Total'],
      data.contractorPerf.map(c => [
        c.contractor, c.hon_qty, c.hl_qty, c.hon_count, c.tokens, c.assoc, c.entries, c.yield, c.share,
      ]),
    );
  };

  const exportEarners = () => {
    if (!data?.topEarners) return;
    downloadCSV(
      `bravo_top_earners_${applied.from}_${applied.to}.csv`,
      ['Employee ID','Name','Contractor','Total Tokens','Days Worked','Last Date'],
      data.topEarners.map(e => [
        e.emp_id, e.emp_name, e.contractor, e.total_tokens, e.days_worked, e.last_date,
      ]),
    );
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const kpi = data?.kpi || {};
  const trend = data?.dailyTrend || [];
  const cpf = data?.contractorPerf || [];
  const shifts = data?.shiftSummary || [];
  const earners = data?.topEarners || [];
  const ctorList = data?.contractors || [];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="brd-wrap">
      {embedded && (
        <div>
          <div className="brd-section-pill"><span>📊 SECTION 3 — BRAVO REPORTS</span></div>
          <div style={{ marginBottom: 4 }}>
            <span className="brd-h1">Bravo Reports</span>
            <span className="brd-livesync">● Live sync</span>
          </div>
          <div className="brd-sub">Summary and analytics — {applied.from} to {applied.to}</div>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div className="brd-filter-bar">
        {/* Daily/Weekly/Monthly preset */}
        <div className="brd-ff" style={{ minWidth: 'auto' }}>
          <label>Quick</label>
          <div className="brd-preset-grp">
            {[
              { v:'daily',   l:'Daily',   i:'📅' },
              { v:'weekly',  l:'Weekly',  i:'📆' },
              { v:'monthly', l:'Monthly', i:'🗓️' },
            ].map(p => (
              <button key={p.v} type="button"
                className={`brd-preset-btn${quickPreset === p.v ? ' active' : ''}`}
                onClick={() => applyPreset(p.v)}>
                {p.i} {p.l}
              </button>
            ))}
          </div>
        </div>
        <div className="brd-ff">
          <label>Date From</label>
          <input type="date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setQuickPreset(''); }} />
        </div>
        <div className="brd-ff">
          <label>Date To</label>
          <input type="date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setQuickPreset(''); }} />
        </div>
        <div className="brd-ff">
          <label>Contractor</label>
          <select value={filterCont} onChange={e => setFilterCont(e.target.value)}>
            <option value="">All Contractors</option>
            {ctorList.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="brd-ff">
          <label>Shift</label>
          <select value={filterShift} onChange={e => setFilterShift(e.target.value)}>
            <option value="">All Shifts</option>
            <option value="DAY">☀️ Day</option>
            <option value="NIGHT">🌙 Night</option>
            <option value="GENERAL">🌗 General</option>
          </select>
        </div>
        <button className="brd-apply-btn" onClick={applyFilters}>▶ Apply</button>
        <button className="brd-reset-btn" onClick={resetFilters}>↺ Reset</button>
      </div>

      {/* ── Export Row ── */}
      <div className="brd-export-row">
        <button className="brd-exp-btn full" onClick={exportFull}>📥 Full Report Export</button>
        <button className="brd-exp-btn cont" onClick={exportContractors}>📋 Contractor Export</button>
        <button className="brd-exp-btn lot"  onClick={exportEarners}>👥 Top Earners Export</button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="brd-kpi-grid">
        {[
          { i:'🏢', l:'Total Contractors', v:fmtN(kpi.tc),       c:'red' },
          { i:'👥', l:'Total Associates',  v:fmtN(kpi.ta),       c:'green' },
          { i:'🪙', l:'Total Tokens',      v:fmtN(kpi.tt),       c:'amber' },
          { i:'📦', l:'Total H.ON (KG)',   v:fmt(kpi.tHON, 0),   c:'blue' },
          { i:'✂️', l:'Deheading (KG)',   v:fmt(kpi.tHL, 0),    c:'green' },
          { i:'🔢', l:'H.ON Count',        v:fmtN(kpi.tHonCount),c:'purple' },
          { i:'📊', l:'Total Entries',     v:fmtN(kpi.tEntries), c:'blue' },
          { i:'📈', l:'Avg Yield %',       v:(kpi.avgYield||0)+'%', c:'green' },
          { i:'💎', l:'Tokens/Associate',  v:fmt(kpi.avgTokPerAssoc, 1), c:'amber' },
          // ADDED Turn 2:
          { i:'⚡', l:'Avg Productivity',   v:fmt(data?.avgProductivity || 0, 1) + ' KG/hr', c:'purple' },
          { i:'🎯', l:'% Above Standard',   v:(data?.incentiveDistribution?.pctAboveStd || 0) + '%', c:'red' },
        ].map((k, i) => (
          <div key={i} className={`brd-kpi-card ${k.c}`}>
            <span className="brd-kpi-icon">{k.i}</span>
            <div>
              <div className="brd-kpi-lbl">{k.l}</div>
              <div className="brd-kpi-val">{loading ? '…' : k.v}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div className="brd-charts-row">
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">📈 Production Trend</span>
            <div className="brd-preset-grp" style={{ marginLeft: 'auto' }}>
              {[
                { v:'hon',    l:'H.ON' },
                { v:'hl',     l:'Deheading' },
                { v:'tokens', l:'Tokens' },
              ].map(t => (
                <button key={t.v} className={`brd-preset-btn${chartTab === t.v ? ' active' : ''}`}
                  onClick={() => setChartTab(t.v)}>{t.l}</button>
              ))}
            </div>
          </div>
          <div className="brd-chart-wrap">
            {trend.length === 0 ? (
              <div className="brd-chart-empty"><span>📈</span><span>No data available for selected timeframe.</span></div>
            ) : (
              <LineChart data={trend} xKey="date" lines={[
                chartTab === 'hl'     ? { key:'hl_qty',  label:'Deheading (KG)' } :
                chartTab === 'tokens' ? { key:'hon_qty', label:'H.ON Qty (KG)'  } :  // dailyTrend has no tokens; show hon
                                        { key:'hon_qty', label:'H.ON Qty (KG)'  }
              ]} colors={chartTab === 'hl' ? ['#059669'] : chartTab === 'tokens' ? ['#d97706'] : ['#1565c0']} />
            )}
          </div>
        </div>

        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">🏆 Contractor Performance (Top 10)</span>
          </div>
          <div className="brd-chart-wrap">
            {cpf.length === 0 ? (
              <div className="brd-chart-empty"><span>🏆</span><span>No data available for selected timeframe.</span></div>
            ) : (
              <BarChart data={cpf.slice(0, 10)} xKey="contractor" horizontal
                bars={[
                  { key:'hon_qty', label:'H.ON Qty' },
                  { key:'hl_qty',  label:'Deheading' },
                ]}
                colors={['#1565c0','#059669']} height={260} />
            )}
          </div>
        </div>
      </div>

      {/* ── Contractor-wise Analysis ── */}
      <div className="brd-card">
        <div className="brd-card-hdr">
          <span className="brd-card-title">📋 Contractor-wise Analysis</span>
          {/* Inline preset filters next to title */}
          <div className="brd-preset-grp" style={{ marginLeft: 10 }}>
            {[
              { v:'daily',   l:'Daily',   i:'📅' },
              { v:'weekly',  l:'Weekly',  i:'📆' },
              { v:'monthly', l:'Monthly', i:'🗓️' },
            ].map(p => (
              <button key={p.v} type="button"
                className={`brd-preset-btn${quickPreset === p.v ? ' active' : ''}`}
                onClick={() => applyPreset(p.v)}>
                {p.i} {p.l}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="brd-pill blue">{cpf.length} contractors</span>
            <button className="brd-exp-btn lot" onClick={exportContractors} style={{ padding: '4px 10px' }}>📥 Export</button>
          </div>
        </div>
        <div className="brd-tbl-wrap">
          {cpf.length === 0 ? (
            <div className="brd-chart-empty" style={{ height: 120 }}>
              <span>📋</span><span>No data available for selected timeframe.</span>
            </div>
          ) : (
            <table className="brd-tbl">
              <thead>
                <tr>
                  <th className="c">#</th>
                  <th>Contractor</th>
                  <th className="r">Entries</th>
                  <th className="r">Associates</th>
                  <th className="r">H.ON Count</th>
                  <th className="r">H.ON Qty (KG)</th>
                  <th className="r">Deheading (KG)</th>
                  <th className="r">Tokens</th>
                  <th className="r">Yield %</th>
                  <th className="r">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {cpf.map((c, i) => (
                  <tr key={c.contractor}>
                    <td className="c" style={{ color:'#9ca3af', fontFamily:"'DM Mono',monospace" }}>{i + 1}</td>
                    <td style={{ fontWeight: 600, color: '#1565c0' }}>{c.contractor}</td>
                    <td className="r">{c.entries}</td>
                    <td className="r">{c.assoc}</td>
                    <td className="r">{c.hon_count}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#1565c0' }}>{fmt(c.hon_qty, 2)}</td>
                    <td className="r" style={{ color: '#059669' }}>{fmt(c.hl_qty, 2)}</td>
                    <td className="r" style={{ color: '#d97706' }}>{fmtN(c.tokens)}</td>
                    <td className="r">
                      <span className={`brd-pill ${c.yield >= 60 ? 'green' : 'blue'}`}>{c.yield.toFixed(1)}%</span>
                    </td>
                    <td className="r">
                      <div style={{ display:'inline-flex', alignItems:'center', gap:6, minWidth:90 }}>
                        <div style={{ flex:1, height:6, background:'#e5e7eb', borderRadius:8, overflow:'hidden', minWidth:50 }}>
                          <div style={{ height:'100%', width:`${Math.min(100, c.share)}%`, background:'linear-gradient(90deg,#059669,#1565c0)', borderRadius:8 }} />
                        </div>
                        <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, minWidth:36, textAlign:'right' }}>{c.share.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>TOTAL ({cpf.length} contractor{cpf.length !== 1 ? 's' : ''})</td>
                  <td className="r">{cpf.reduce((s, c) => s + (c.entries || 0), 0)}</td>
                  <td className="r">{cpf.reduce((s, c) => s + (c.assoc || 0), 0)}</td>
                  <td className="r">{fmtN(cpf.reduce((s, c) => s + (c.hon_count || 0), 0))}</td>
                  <td className="r" style={{ color: '#1565c0' }}>{fmt(cpf.reduce((s, c) => s + (c.hon_qty || 0), 0), 2)}</td>
                  <td className="r" style={{ color: '#059669' }}>{fmt(cpf.reduce((s, c) => s + (c.hl_qty || 0), 0), 2)}</td>
                  <td className="r" style={{ color: '#d97706' }}>{fmtN(cpf.reduce((s, c) => s + (c.tokens || 0), 0))}</td>
                  <td className="r">—</td>
                  <td className="r">100%</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* ── Shift-wise Analysis ── */}
      {shifts.length > 0 && (
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">🌓 Shift-wise Analysis</span>
            <span className="brd-pill blue" style={{ marginLeft: 10 }}>{shifts.length} shift{shifts.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="brd-tbl-wrap">
            <table className="brd-tbl">
              <thead>
                <tr>
                  <th>Shift</th>
                  <th className="r">Entries</th>
                  <th className="r">H.ON Count</th>
                  <th className="r">H.ON Qty (KG)</th>
                  <th className="r">Deheading (KG)</th>
                  <th className="r">Tokens</th>
                  <th className="r">Yield %</th>
                </tr>
              </thead>
              <tbody>
                {shifts.map(s => (
                  <tr key={s.shift}>
                    <td style={{ fontWeight: 700, color: s.shift === 'DAY' ? '#d97706' : s.shift === 'NIGHT' ? '#1565c0' : '#7c3aed' }}>
                      {s.shift === 'DAY' ? '☀️ DAY' : s.shift === 'NIGHT' ? '🌙 NIGHT' : '🌗 GENERAL'}
                    </td>
                    <td className="r">{s.entries}</td>
                    <td className="r">{fmtN(s.hon_count)}</td>
                    <td className="r" style={{ color: '#1565c0', fontWeight: 700 }}>{fmt(s.hon_qty, 2)}</td>
                    <td className="r" style={{ color: '#059669' }}>{fmt(s.hl_qty, 2)}</td>
                    <td className="r" style={{ color: '#d97706' }}>{fmtN(s.tokens)}</td>
                    <td className="r">
                      <span className={`brd-pill ${s.yield >= 60 ? 'green' : 'blue'}`}>{s.yield.toFixed(1)}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Top Earners (per-employee) ── */}
      {earners.length > 0 && (
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">🏆 Top Earners (Employee Tokens)</span>
            <span className="brd-pill green" style={{ marginLeft: 10 }}>{earners.length} earners</span>
            <button className="brd-exp-btn lot" onClick={exportEarners} style={{ marginLeft: 'auto', padding: '4px 10px' }}>📥 Export</button>
          </div>
          <div className="brd-tbl-wrap">
            <table className="brd-tbl">
              <thead>
                <tr>
                  <th className="c">#</th>
                  <th>Employee Name</th>
                  <th>Employee ID</th>
                  <th>Contractor</th>
                  <th className="r">Total Tokens</th>
                  <th className="r">Days Worked</th>
                  <th>Last Date</th>
                </tr>
              </thead>
              <tbody>
                {earners.map((e, i) => (
                  <tr key={`${e.emp_id}-${i}`}>
                    <td className="c" style={{ color: '#9ca3af' }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{e.emp_name || 'Unknown'}</td>
                    <td style={{ fontFamily:"'DM Mono',monospace", color: '#6b7280', fontSize: 11 }}>{e.emp_id}</td>
                    <td style={{ color: '#1565c0' }}>{e.contractor}</td>
                    <td className="r" style={{ fontWeight: 700, color: '#d97706' }}>{fmtN(e.total_tokens)}</td>
                    <td className="r">{e.days_worked}</td>
                    <td style={{ fontFamily:"'DM Mono',monospace", color: '#6b7280', fontSize: 11 }}>{e.last_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════ */}
      {/* ADDED Turn 2: Smart Insights + Shift Perf + Incentive Analytics */}
      {/* + Productivity Trend + Associate Composition                   */}
      {/* ══════════════════════════════════════════════════════════════ */}

      {/* ── Smart Insights ── */}
      {data && (kpi.tEntries || 0) > 0 && (() => {
        const cpfBest    = cpf.length ? cpf[0] : null;
        const cpfWorst   = cpf.length ? [...cpf].sort((a,b) => (a.yield||0) - (b.yield||0))[0] : null;
        const bestShift  = shifts.length ? [...shifts].sort((a,b) => (b.yield||0) - (a.yield||0))[0] : null;
        const bestProd   = cpf.length ? [...cpf].sort((a,b) => (b.productivity||0) - (a.productivity||0))[0] : null;
        const topExtras  = (data.contractorExtras || [])[0];
        const dist       = data.incentiveDistribution || {};

        const insights = [
          cpfBest && {
            bg:'#ecfdf5', bd:'#059669', icon:'🏆',
            text:<><strong>Top Contractor:</strong> {cpfBest.contractor} — {fmt(cpfBest.hon_qty, 0)} KG H.ON</>,
          },
          bestShift && {
            bg:'#dbeafe', bd:'#1565c0', icon:'⚡',
            text:<><strong>Best Shift:</strong> {bestShift.shift} — {bestShift.yield.toFixed(1)}% yield</>,
          },
          topExtras && {
            bg:'#fef3c7', bd:'#d97706', icon:'🪙',
            text:<><strong>Top Incentives:</strong> {topExtras.contractor} — +{fmtN(topExtras.extras)} extra tokens</>,
          },
          cpfWorst && cpfWorst.contractor !== (cpfBest && cpfBest.contractor) && {
            bg:'#fee2e2', bd:'#dc2626', icon:'⚠',
            text:<><strong>Lowest Yield:</strong> {cpfWorst.contractor} — {(cpfWorst.yield||0).toFixed(1)}%</>,
          },
          bestProd && (bestProd.productivity || 0) > 0 && {
            bg:'#ede9fe', bd:'#7c3aed', icon:'⚙',
            text:<><strong>Top Productivity:</strong> {bestProd.contractor} — {fmt(bestProd.productivity, 1)} KG/hr</>,
          },
          {
            bg:'#f0fdf4', bd:'#16a34a', icon:'✓',
            text:<><strong>Activity:</strong> {kpi.tEntries} entries · {cpf.length} contractor{cpf.length!==1?'s':''} · {dist.totalEmps || 0} employees tracked</>,
          },
        ].filter(Boolean);

        return (
          <div className="brd-card">
            <div className="brd-card-hdr">
              <span className="brd-card-title">💡 Smart Insights</span>
              <span className="brd-pill blue" style={{ marginLeft: 10 }}>auto-generated</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:8, padding:14 }}>
              {insights.map((ins, i) => (
                <div key={i} style={{ padding:'10px 12px', background:ins.bg, borderLeft:`3px solid ${ins.bd}`, borderRadius:6, fontSize:11, color:'#1f2937', display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ fontSize:16 }}>{ins.icon}</span>
                  <span style={{ flex:1 }}>{ins.text}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Shift Performance — grouped bar chart ── */}
      {shifts.length > 0 && (
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">🌓 Shift Performance Comparison</span>
            <span className="brd-pill blue" style={{ marginLeft: 10 }}>{shifts.length} shift{shifts.length!==1?'s':''}</span>
          </div>
          {/* FIX: max-width + reduced height — was stretching full screen */}
          <div className="brd-chart-wrap" style={{ minHeight: 200, maxWidth: 640, margin: '0 auto' }}>
            <BarChart data={shifts} xKey="shift" bars={[
              { key:'tokens',       label:'Tokens' },
              { key:'hl_qty',       label:'Deheading' },
              { key:'productivity', label:'Productivity' },
              { key:'associates',   label:'Associates' },
            ]} colors={['#d97706','#059669','#1565c0','#7c3aed']} height={200} />
          </div>
        </div>
      )}

      {/* ── Incentive Analytics row ── */}
      {data && (data.incentiveDistribution?.totalEmps > 0 || (data.contractorExtras || []).length > 0) && (
        <div className="brd-charts-row">
          {/* Contractor-wise Extras chart */}
          <div className="brd-card">
            <div className="brd-card-hdr">
              <span className="brd-card-title">🎁 Contractor-wise Extra Tokens</span>
            </div>
            <div className="brd-chart-wrap" style={{ minHeight: 240 }}>
              {(data.contractorExtras || []).length === 0 ? (
                <div className="brd-chart-empty"><span>🎁</span><span>No incentive earners in selected timeframe.</span></div>
              ) : (
                <BarChart data={(data.contractorExtras || []).slice(0, 10)} xKey="contractor" horizontal
                  bars={[{ key:'extras', label:'Extra Tokens' }]}
                  colors={['#d97706']} height={240} />
              )}
            </div>
          </div>

          {/* Distribution stat panel */}
          <div className="brd-card">
            <div className="brd-card-hdr">
              <span className="brd-card-title">📊 Incentive Distribution</span>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom: 14 }}>
                <div style={{ fontSize:36, fontWeight:700, color:'#059669', fontFamily:"'DM Mono',monospace", lineHeight:1 }}>
                  {data.incentiveDistribution?.pctAboveStd || 0}%
                </div>
                <div style={{ fontSize:11, color:'#6b7280', lineHeight:1.5 }}>
                  Of <strong>{data.incentiveDistribution?.totalEmps || 0} employees</strong> earned tokens above the 60-token standard.<br/>
                  Avg extra: <strong>+{data.incentiveDistribution?.avgExtra || 0} tokens/employee</strong>
                </div>
              </div>
              <div style={{ display:'flex', height:10, borderRadius:6, overflow:'hidden', background:'#fee2e2', marginBottom:8 }}>
                <div style={{ width:`${data.incentiveDistribution?.pctAboveStd || 0}%`, background:'linear-gradient(90deg,#059669,#16a34a)' }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#6b7280' }}>
                <span>🟢 {data.incentiveDistribution?.aboveStd || 0} above</span>
                <span>🔴 {data.incentiveDistribution?.belowStd || 0} below</span>
              </div>
              <div style={{ marginTop: 14, padding:'10px 12px', background:'#f8f9fa', borderRadius:6, fontSize:11, color:'#374151' }}>
                💰 <strong>Total extras pool:</strong> {fmtN(data.incentiveDistribution?.totalExtras || 0)} tokens
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Productivity Trend ── */}
      {(data?.productivityTrend || []).length > 0 && (
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">⚡ Productivity Trend (KG/hr by date)</span>
            <span className="brd-pill blue" style={{ marginLeft: 10 }}>
              Total hours: {fmt(data.totalHours || 0, 1)}
            </span>
          </div>
          {/* FIX: max-width + reduced height — was stretching full screen */}
          <div className="brd-chart-wrap" style={{ minHeight: 180, maxWidth: 640, margin: '0 auto' }}>
            <LineChart data={data.productivityTrend} xKey="date"
              lines={[{ key:'productivity', label:'KG/hr' }]}
              colors={['#7c3aed']} height={180} />
          </div>
        </div>
      )}

      {/* ── Associate Composition ── */}
      {(data?.associateComposition || []).length > 0 && (
        <div className="brd-card">
          <div className="brd-card-hdr">
            <span className="brd-card-title">👥 Associate Composition</span>
            <span className="brd-pill blue" style={{ marginLeft: 10 }}>
              {(data.associateComposition || []).reduce((s,r) => s + r.total, 0)} total
            </span>
          </div>
          {/* FIX: max-width + reduced height — was stretching full screen */}
          <div className="brd-chart-wrap" style={{ minHeight: 220, maxWidth: 640, margin: '0 auto' }}>
            <BarChart data={(data.associateComposition || []).slice(0, 10)} xKey="contractor" horizontal
              bars={[
                { key:'assoc_f', label:'Female Associates' },
                { key:'supp_m',  label:'Support Male' },
                { key:'supp_f',  label:'Support Female' },
              ]}
              colors={['#1565c0','#fbbf24','#f59e0b']} height={220} />
          </div>
        </div>
      )}

      {/* Empty state when no data at all */}
      {!loading && data && (kpi.tEntries || 0) === 0 && (
        <div className="brd-card">
          <div className="brd-chart-empty" style={{ height: 180 }}>
            <span>📭</span>
            <span>No data available for selected timeframe.</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Try a wider date range or check Bravo entries</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BravoReportDashboard;
